import {
  BackButton,
  Icon,
  ScopeBadge,
  Text,
  Notes,
  Reminders,
  Memories,
  ProfileSkeleton,
  DeleteModal,
  VoiceRecordButton,
  GenericSelectionModal,
} from '@components'
import { Screen } from '../../../components/templates/Screen'
import { useIsFocused, useNavigation, useRoute, useTheme } from '@react-navigation/native'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Animated, Linking, ScrollView, TouchableOpacity, View } from 'react-native'
import { ProfileImage } from '../../profile/components'
import { GhostActionButtons, About } from './components'

import { getCommonStyles, Scope } from '@utils'
import { AddContactModal } from '../addContacts/components'
import {
  CalendarEvent,
  ContactRequestFilterType,
  Profile,
  useCreationStackIntegration,
  useDeleteGhostProfile,
  useDeleteContact,
  useGetProfileById,
  usePaginateCategories,
  usePaginateHashtags,
  usePaginateRequestedContacts,
  useResolveLinkedinProfile,
  useScreen,
  useSendContactRequest,
  useUpdateContactScope,
  useUpdateGhostProfile,
  useUpdateGhostProfileScope,
} from '@hooks'
import { useCurrentUserStore } from '@store'
import { useLanguage } from '@context'
import { isSameArrays, SelectionItem } from '@utils'
import { getDetailProfileStyle } from './styles'
import AddGhostForm from '../addGhostContact/components/AddGhostForm'
import { useAddGhostContact } from '../addGhostContact/hooks'
import dayjs from 'dayjs'

export const DetailsGhostProfile: React.FC = () => {
  const theme = useTheme()
  const styles = getDetailProfileStyle(theme)
  const commonStyles = getCommonStyles(theme)
  const { isMobile, isWeb } = useScreen()
  const { t } = useLanguage()
  const isFocused = useIsFocused()

  const route: any = useRoute()
  const { isCreationStackEnabled, showReminderCreation, showNoteCreation } =
    useCreationStackIntegration()

  // Check if we're in modal context
  const payload = route.params?.payload
  const isExtension = !!payload
  // In extension mode, the hash id is not a real KeepTouch id. Resolve first.
  const [profileId, setProfileId] = useState<string>(route.params?.profileId)
  const { data: profileData, isFetching } = useGetProfileById(profileId)
  const [profile, setProfile] = useState<Profile | undefined>()
  const [isAddContactModalVisible, setIsAddContactModalVisible] = useState(false)
  const [isRequestPendingLocal, setIsRequestPendingLocal] = useState(false)
  const [selectedTab, setSelectedTab] = useState<'about' | 'note' | 'reminder' | 'memory'>('about')
  const [showBackButton, setShowBackButton] = useState(true)
  const [scope, setScope] = useState<Scope>(profileData?.scope || Scope.PRIVATE)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)

  const { formData, handleFormChange, handleSave } = useAddGhostContact(profile)

  const [isFloatingExpanded, setIsFloatingExpanded] = useState(true)
  const floatingAnim = useRef(new Animated.Value(1)).current
  const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const collapseFloating = () => {
    setIsFloatingExpanded(false)
    Animated.timing(floatingAnim, { toValue: 0, duration: 200, useNativeDriver: true }).start()
  }

  const expandFloating = () => {
    setIsFloatingExpanded(true)
    Animated.timing(floatingAnim, { toValue: 1, duration: 200, useNativeDriver: true }).start()
  }

  useEffect(() => {
    if (!isMobile || !profileId) return
    collapseTimer.current = setTimeout(collapseFloating, 3000)
    return () => {
      if (collapseTimer.current) clearTimeout(collapseTimer.current)
    }
  }, [isMobile, profileId])

  const [hashtagsSearchText, setHashtagsSearchText] = useState('')
  const [categoriesSearchText, setCategoriesSearchText] = useState('')
  const [isHashtagsModalVisible, setIsHashtagsModalVisible] = useState(false)
  const [isCategoriesModalVisible, setIsCategoriesModalVisible] = useState(false)
  const [selectedHashtags, setSelectedHashtags] = useState<SelectionItem[]>(
    profile?.pinsHashtags?.map((h) => ({ id: h.id, name: h.name })) || [],
  )
  const [selectedCategories, setSelectedCategories] = useState<SelectionItem[]>(
    profile?.pinsCategories?.map((c) => ({ id: c.id, name: c.name, photo: c.avatarUrl || '' })) ||
      [],
  )

  const {
    flatData: hashtags,
    isFetching: isFetchingHashtags,
    isFetchingNextPage: isFetchingNextPageHashtags,
    fetchNextPage: fetchNextPageHashtags,
    hasNextPage: hasNextPageHashtags,
  } = usePaginateHashtags(hashtagsSearchText)
  const {
    flatData: categories,
    isFetching: isFetchingCategories,
    isFetchingNextPage: isFetchingNextPageCategories,
    fetchNextPage: fetchNextPageCategories,
    hasNextPage: hasNextPageCategories,
  } = usePaginateCategories(categoriesSearchText)
  const { mutate: updateGhostProfile } = useUpdateGhostProfile()
  const { mutate: updateGhostProfileScope } = useUpdateGhostProfileScope()

  const { mutateAsync: resolveLinkedinProfile } = useResolveLinkedinProfile()
  const { mutate: sendContactRequest } = useSendContactRequest()
  const { mutate: updateContactScope } = useUpdateContactScope()
  const { mutateAsync: deleteGhostProfile } = useDeleteGhostProfile()
  const { mutateAsync: deleteContact } = useDeleteContact()
  const { data: sentRequests } = usePaginateRequestedContacts(ContactRequestFilterType.SENT)
  const navigation = useNavigation()
  const { userID } = useCurrentUserStore()
  const isMe = profile?.accountId === userID

  useEffect(() => {
    const fetchProfile = async () => {
      if (profileId && profileData) {
        setProfile(profileData)
        if (profileData.scope) {
          setScope(profileData.scope)
        }
      } else {
        try {
          const data = JSON.parse(atob(payload))
          const newProfile = {
            professions: [],
            fullName: data.name,
            // optimistic local parsing; final ID + names come from the API resolve
            firstName: data.name?.split(' ')?.[0],
            lastName: data.name?.split(' ')?.slice(1).join(' '),
            avatarUrl: data.image,
            gender: 'male',
            birthDate: dayjs().format('DD-MM-YYYY'),
            isGhost: true,
          }
          setProfile(newProfile as unknown as Profile)
          handleFormChange('birthDate', dayjs().format('DD-MM-YYYY'))
          // Resolve/Upsert in DB then switch to the canonical id.
          // Extension payloads include platform/handle/profileUrl so the API can
          // dedupe across social networks (linkedin/facebook/twitter/instagram).
          const resolved = await resolveLinkedinProfile({
            fullName: data.name,
            avatarUrl: data.image,
            bio: data.title || undefined,
            platform: data.platform,
            handle: data.handle,
            profileUrl: data.profileUrl,
          })

          if (resolved?.id) {
            if (resolved.kind === 'ghost') {
              handleFormChange('id', resolved.id)
            } else {
              setProfileId(resolved.id)
            }
          }
        } catch {
          // If not logged in, axios interceptor should try background login.
          // We keep the local extension profile in UI until retry succeeds.
        }
      }
    }
    fetchProfile()
  }, [profileId, profileData])

  useEffect(() => {
    if (profile?.pinsHashtags) {
      setSelectedHashtags(profile.pinsHashtags.map((h) => ({ id: h.id, name: h.name })))
    }
    if (profile?.pinsCategories) {
      setSelectedCategories(
        profile.pinsCategories.map((c) => ({ id: c.id, name: c.name, photo: c.avatarUrl || '' })),
      )
    }
  }, [profile?.pinsHashtags, profile?.pinsCategories])

  useEffect(() => {
    if (!isFocused) {
      setSelectedHashtags([])
      setSelectedCategories([])
      setIsHashtagsModalVisible(false)
      setIsCategoriesModalVisible(false)
    }
  }, [isFocused])

  const isGhost = !profileId || profile?.isGhost
  const isPendingFromServer = useMemo(() => {
    if (!profileId) return false
    return (
      sentRequests?.pages.some((page) =>
        page.data.some(
          (request) => request.recipient.id === profileId && request.status === 'PENDING',
        ),
      ) ?? false
    )
  }, [profileId, sentRequests])
  const isRequestPending = (isRequestPendingLocal || isPendingFromServer) && !profile?.scope

  const beforeSaving = () => {
    setProfileId(formData.id)
    handleSave()
  }

  const onScopeChange = (newScope: Scope) => {
    setScope(newScope)
    setProfile((prev) => (prev ? { ...prev, scope: newScope } : prev))
    if (!profileId) return
    if (isGhost) {
      updateGhostProfileScope({ ghostProfileId: profileId, scope: newScope })
    } else {
      updateContactScope({ contactId: profileId, scope: newScope })
    }
  }

  const onAddPress = () => {
    setIsAddContactModalVisible(true)
  }

  const handleAddPrivate = () => {
    sendContactRequest(
      {
        recipientId: profileId,
        scope: Scope.PRIVATE,
      },
      {
        onSuccess: () => {
          setIsRequestPendingLocal(true)
          setIsAddContactModalVisible(false)
        },
      },
    )
  }

  const handleAddProfessional = () => {
    sendContactRequest(
      {
        recipientId: profileId,
        scope: Scope.PROFESSIONAL,
      },
      {
        onSuccess: () => {
          setIsRequestPendingLocal(true)
          setIsAddContactModalVisible(false)
        },
      },
    )
  }

  const handleEventPress = (event: CalendarEvent) => {
    showReminderCreation({
      isCreationStack: true,
      eventId: event.id,
      contactId: profileId,
    })
  }

  const handleNotePress = (noteId: string) => {
    showNoteCreation({
      isCreationStack: true,
      noteId: noteId,
      contactId: profileId,
      isGhost: isGhost || !profile?.scope,
    })
  }

  const handleScroll = (event: any) => {
    const scrollY = event.nativeEvent.contentOffset.y
    setShowBackButton(scrollY <= 10) // Show back button when scrolled less than 10px from top
  }

  const openKeepTouch = () => {
    Linking.openURL('https://keeptouch.co/profile/' + (profileId || formData.id))
  }

  const handleDeleteConfirm = async () => {
    if (!profileId) return
    try {
      if (isGhost) {
        await deleteGhostProfile(profileId)
      } else {
        await deleteContact({ id: profileId })
      }
      setIsDeleteModalVisible(false)
      ;(navigation as any).goBack?.()
    } catch {
      // Error could be shown via toast if needed
    }
  }

  return isFetching ? (
    <Screen>
      <ProfileSkeleton />
    </Screen>
  ) : (
    <Screen>
      {showBackButton && !payload && !isCategoriesModalVisible && !isHashtagsModalVisible && (
        <BackButton title={t('common.back')} noOffset={!isWeb} />
      )}
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={[
          styles.contentContainer,
          showBackButton && isWeb && commonStyles.paddingTop,
        ]}
        onScroll={handleScroll}
        scrollEventThrottle={16}
      >
        {isExtension && (
          <TouchableOpacity
            disabled={!profileId}
            onPress={openKeepTouch}
            style={[
              commonStyles.row,
              commonStyles.alignSelfEnd,
              commonStyles.paddingHorizontal,
              commonStyles.paddingTop,
              commonStyles.paddingBottom,
            ]}
          >
            <Text type="bodySmall" color={theme.colors.primary}>
              {t('ghostProfile.openOnKeepTouch')}
            </Text>
            <Icon name="openInNew" size={theme.iconSize.small} color={theme.colors.primary} />
          </TouchableOpacity>
        )}
        <View style={styles.content}>
          <View style={styles.rightSection}>
            {profile?.scope && <ScopeBadge scope={[scope]} onChange={onScopeChange} />}
          </View>
          <View style={styles.infoContainer}>
            <ProfileImage profileId={profileId} isGhost={isGhost} avatarUrl={profile?.avatarUrl} />
            <View style={styles.infoTextContainer}>
              <View style={[styles.titleContainer, isExtension && styles.titleContainerExtension]}>
                <Text type="titleBold" style={styles.name}>
                  {profile?.fullName}
                </Text>
              </View>
              <Text type="body" color={theme.colors.primary} style={styles.work}>
                {profile?.contacts?.email ||
                  profile?.contacts?.phone ||
                  profile?.contacts?.website ||
                  profile?.contacts?.facebook ||
                  profile?.contacts?.instagram ||
                  profile?.contacts?.twitter ||
                  profile?.contacts?.linkedin}
              </Text>
              <View style={styles.row}>
                <View style={styles.locationContainer}>
                  <Icon
                    name="work"
                    size={theme.iconSize.extraSmall}
                    color={theme.colors.greyBlue}
                  />
                  <Text type="body" color={theme.colors.greyBlue} style={styles.locationText}>
                    {profile?.professions?.map((p) => p.name).join(' & ') || 'Freelance'}
                  </Text>
                </View>
                {profile?.residence && (
                  <View style={styles.locationContainer}>
                    <Icon
                      name="mapPin"
                      size={theme.iconSize.extraSmall}
                      color={theme.colors.greyBlue}
                    />
                    <Text type="body" color={theme.colors.greyBlue} style={styles.locationText}>
                      {profile?.residence?.name ?? profile?.residence?.nameFr}
                    </Text>
                  </View>
                )}
              </View>
            </View>
          </View>
          <GhostActionButtons
            isGhost={isGhost}
            isExtension={isExtension && !profileId}
            contact={profile!}
            isRequestPending={isRequestPending}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            onAddPress={onAddPress}
            onHashtagsPress={() => setIsHashtagsModalVisible(true)}
            onCategoriesPress={() => setIsCategoriesModalVisible(true)}
          />
          <View style={!isMobile && styles.container}>
            {selectedTab === 'about' &&
              (!profileId ? (
                <AddGhostForm
                  formData={formData}
                  handleFormChange={handleFormChange}
                  handleSave={beforeSaving}
                />
              ) : (
                <About profile={profile} />
              ))}
            {selectedTab === 'note' && (
              <Notes
                profileId={profileId}
                isGhost={isGhost || !profile?.scope}
                onNotePress={isCreationStackEnabled ? handleNotePress : undefined}
              />
            )}
            {selectedTab === 'reminder' && (
              <Reminders
                selectedContactId={profileId}
                contactAccountId={profile?.accountId}
                birthDate={profile?.birthDate}
                isGhost={isGhost || !profile?.scope}
                withAddButton
                onEventPress={isCreationStackEnabled ? handleEventPress : undefined}
              />
            )}
            {selectedTab === 'memory' && (
              <Memories
                profileId={profileId}
                contactAccountId={profile?.accountId ?? (isGhost ? profileId : undefined)}
              />
            )}
          </View>
        </View>
      </ScrollView>
      {isMobile && profileId && (
        <View style={styles.floatingPanel}>
          <Animated.View
            pointerEvents={isFloatingExpanded ? 'auto' : 'none'}
            style={[
              styles.floatingContent,
              {
                opacity: floatingAnim,
                transform: [
                  {
                    translateY: floatingAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: [12, 0],
                    }),
                  },
                ],
              },
            ]}
          >
            {!isMe && <VoiceRecordButton profileId={profileId} />}
            <TouchableOpacity
              style={styles.floatingDeleteButton}
              onPress={() => setIsDeleteModalVisible(true)}
            >
              <Icon name="trash" size={theme.iconSize.small} color={theme.colors.error} />
            </TouchableOpacity>
          </Animated.View>
          <TouchableOpacity
            style={styles.floatingToggle}
            onPress={isFloatingExpanded ? collapseFloating : expandFloating}
          >
            <Icon
              name={isFloatingExpanded ? 'arrowDown' : 'arrowUp'}
              size={theme.iconSize.small}
              color={theme.colors.secondary}
            />
          </TouchableOpacity>
        </View>
      )}

      {!isMobile && profileId && (
        <View
          style={[
            commonStyles.alignSelfEnd,
            commonStyles.paddingTop,
            commonStyles.paddingBottom,
            commonStyles.paddingHorizontal,
          ]}
        >
          <TouchableOpacity onPress={() => setIsDeleteModalVisible(true)}>
            <Text type="bodySmall" color={theme.colors.error}>
              {isGhost ? t('ghostProfile.deleteProfile') : t('contacts.uncontact')}
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {!isMobile && !isMe && profileId && (
        <View
          style={[
            commonStyles.absoluteBottom,
            commonStyles.alignStart,
            commonStyles.paddingTop,
            commonStyles.paddingBottom,
            commonStyles.paddingHorizontal,
          ]}
        >
          <VoiceRecordButton profileId={profileId} />
        </View>
      )}
      <AddContactModal
        isVisible={isAddContactModalVisible}
        setIsVisible={(visible) =>
          setIsAddContactModalVisible(
            typeof visible === 'function' ? visible(isAddContactModalVisible) : visible,
          )
        }
        contactName={profile?.fullName || ''}
        onAddPrivate={handleAddPrivate}
        onAddProfessional={handleAddProfessional}
      />
      <DeleteModal
        isVisible={isDeleteModalVisible}
        setIsVisible={setIsDeleteModalVisible}
        title={isGhost ? t('ghostProfile.deleteConfirmTitle') : t('contacts.uncontactConfirmTitle')}
        onDelete={handleDeleteConfirm}
      />
      <GenericSelectionModal
        isVisible={isHashtagsModalVisible}
        setIsVisible={setIsHashtagsModalVisible}
        title={t('ghostProfile.hashtags')}
        placeholder={t('ghostProfile.searchHashtags')}
        availableItems={hashtags}
        selectedItems={selectedHashtags}
        onSelectionChange={setSelectedHashtags}
        onClosed={() => {
          if (
            profile?.isGhost &&
            !isSameArrays(
              profile.pinsHashtags.map((h) => h.id),
              selectedHashtags.map((h) => h.id),
            )
          ) {
            updateGhostProfile({ id: profile.id, pinsHashtags: selectedHashtags.map((h) => h.id) })
          }
        }}
        isHashtagsModal
        isFetching={isFetchingHashtags}
        fetchNextPage={fetchNextPageHashtags}
        hasNextPage={hasNextPageHashtags}
        isFetchingNextPage={isFetchingNextPageHashtags}
        onSearch={setHashtagsSearchText}
        fitParent
      />
      <GenericSelectionModal
        isVisible={isCategoriesModalVisible}
        setIsVisible={setIsCategoriesModalVisible}
        title={t('ghostProfile.categories')}
        placeholder={t('ghostProfile.searchCategories')}
        availableItems={categories.map((c) => ({
          id: c.id,
          name: c.name,
          photo: c.avatarUrl || '',
        }))}
        selectedItems={selectedCategories}
        onSelectionChange={setSelectedCategories}
        onClosed={() => {
          if (
            profile?.isGhost &&
            !isSameArrays(
              profile.pinsCategories.map((c) => c.id),
              selectedCategories.map((c) => c.id),
            )
          ) {
            updateGhostProfile({
              id: profile.id,
              pinsCategories: selectedCategories.map((c) => c.id),
            })
          }
        }}
        isCategoriesModal
        isFetching={isFetchingCategories}
        fetchNextPage={fetchNextPageCategories}
        hasNextPage={hasNextPageCategories}
        isFetchingNextPage={isFetchingNextPageCategories}
        onSearch={setCategoriesSearchText}
        fitParent
      />
    </Screen>
  )
}
